/**
 * TrialFunnelPage.js (PR_RUN_045 — TRL-035 frontend)
 * ===================================================
 * Admin-only dashboard rendering the trial → upgrade funnel sourced from
 * `GET /api/trial/funnel/aggregates`. Pairs with PR_RUN_043 backend
 * consolidation into `operational_event_logs`.
 *
 * Layout (top → bottom):
 *   1. Window selector (7 / 30 / 90 / 365 days) + refresh button + total
 *      events / unique users / median time-to-upgrade KPI strip.
 *   2. Funnel drop-off — pure SVG horizontal trapezoids per canonical
 *      stage with retention % between each step and dropped count.
 *      Pure SVG (no new dep) — recharts' `<Sankey>` doesn't render
 *      ordinal funnels cleanly.
 *   3. Source-attribution donut — recharts `<PieChart>` with `<Cell>`
 *      shading per `trial_source` (self_signup / book_qr / enterprise_invite / unknown).
 *   4. Raw stage-counts grid + sample event-id list for forensic linkbacks
 *      into the Audit-Trail Explorer.
 *
 * Access: admin-only (route gate in App.js + same gate enforced by the
 * backend endpoint).
 *
 * Authored: 2026-05-26 (E1, PR_RUN_045)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";
import QMSLayout from "../components/QMSLayout";
import { API, useAuth } from "../App";
import {
  TrendingDown, RefreshCw, AlertTriangle, Users, Activity, Clock,
  ArrowRight, ExternalLink,
} from "lucide-react";

const DAYS_OPTIONS = [7, 30, 90, 365];

const STAGE_META = {
  signup:           { label: "Signup",          color: "#38BDF8" },
  checkout_intent:  { label: "Checkout intent", color: "#A78BFA" },
  checkout_success: { label: "Checkout paid",   color: "#10B981" },
  upgraded:         { label: "Upgraded",        color: "#C9A84C" },
  checkout_cancel:  { label: "Checkout cancel", color: "#F87171" },
  trial_expired:    { label: "Trial expired",   color: "#8B95A5" },
};

const FUNNEL_ORDER = ["signup", "checkout_intent", "checkout_success", "upgraded"];

const SOURCE_PALETTE = ["#38BDF8", "#C9A84C", "#A78BFA", "#10B981", "#F87171", "#8B95A5"];

const fmtHours = (h) => {
  if (h === null || h === undefined) return "—";
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} d`;
};

const fmtPct = (p) => `${p.toFixed(1)}%`;

const KpiCard = ({ icon: Icon, label, value, hint, color = "#C9A84C" }) => (
  <div
    className="rounded-xl border border-[rgba(255,255,255,0.06)] bg-[#181B23] p-4"
    data-testid={`trial-funnel-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[10px] uppercase tracking-widest text-[#8B95A5]">{label}</p>
        <p className="text-2xl font-semibold text-white mt-1" style={{ color }}>
          {value}
        </p>
        {hint && <p className="text-[11px] text-[#8B95A5] mt-1">{hint}</p>}
      </div>
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}14`, color }}
      >
        <Icon className="w-4 h-4" />
      </div>
    </div>
  </div>
);

const FunnelBar = ({ stage, count, maxCount, prevCount, isFirst }) => {
  const meta = STAGE_META[stage];
  const widthPct = maxCount > 0 ? Math.max((count / maxCount) * 100, 4) : 4;
  const retention = prevCount > 0 ? (count / prevCount) * 100 : null;
  const dropped = prevCount > 0 ? Math.max(prevCount - count, 0) : 0;

  return (
    <div className="space-y-1" data-testid={`trial-funnel-stage-${stage}`}>
      {!isFirst && retention !== null && (
        <div className="flex items-center gap-2 ml-2 text-[10px] text-[#8B95A5]">
          <ArrowRight className="w-3 h-3" />
          <span data-testid={`trial-funnel-retention-${stage}`}>
            {fmtPct(retention)} retained
          </span>
          {dropped > 0 && (
            <span className="text-[#F87171]">· {dropped} dropped</span>
          )}
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className="w-32 text-[12px] text-white shrink-0 flex items-center gap-2">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ background: meta.color }}
          />
          {meta.label}
        </div>
        <div className="flex-1 h-8 bg-[#0D0F14] rounded relative overflow-hidden">
          <div
            className="h-full rounded transition-all duration-700"
            style={{
              width: `${widthPct}%`,
              background: `linear-gradient(90deg, ${meta.color}AA 0%, ${meta.color}66 100%)`,
              boxShadow: `0 0 0 1px ${meta.color}40 inset`,
            }}
          />
          <div className="absolute inset-0 flex items-center px-3 text-[12px] font-mono">
            <span className="text-white font-semibold" data-testid={`trial-funnel-count-${stage}`}>
              {count}
            </span>
            <span className="text-[#8B95A5] ml-2">unique users</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const TrialFunnelPage = () => {
  const { isAdmin } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAggregates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(
        `${API}/trial/funnel/aggregates?days=${days}`,
        { credentials: "include" },
      );
      if (!r.ok) {
        setError(`HTTP ${r.status}`);
        return;
      }
      setData(await r.json());
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    if (isAdmin) fetchAggregates();
  }, [isAdmin, fetchAggregates]);

  const stageCounts = data?.stage_counts || {};
  const funnelMax = useMemo(() => Math.max(
    1, ...FUNNEL_ORDER.map((s) => stageCounts[s] || 0),
  ), [stageCounts]);

  const sourcePieData = useMemo(() => {
    const sa = data?.source_attribution || {};
    return Object.entries(sa)
      .map(([k, v]) => ({ name: k, value: v }))
      .sort((a, b) => b.value - a.value);
  }, [data]);

  const totalAttributed = useMemo(
    () => sourcePieData.reduce((a, b) => a + b.value, 0),
    [sourcePieData],
  );

  if (!isAdmin) {
    return (
      <QMSLayout title="Trial Funnel">
        <div className="p-8 text-center text-[#8B95A5]">
          <AlertTriangle className="w-8 h-8 mx-auto mb-3 text-[#F87171]" />
          <p>Trial funnel telemetry is restricted to admin users.</p>
        </div>
      </QMSLayout>
    );
  }

  return (
    <QMSLayout title="Trial Funnel">
      <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6" data-testid="trial-funnel-page">
        {/* Header */}
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <TrendingDown size={22} className="text-[#C9A84C]" />
              Trial Funnel
            </h1>
            <p className="text-[12px] text-[#8B95A5] mt-1 max-w-xl">
              Signup → checkout → upgrade drop-off (TRL-035). Sourced from{" "}
              <code className="text-[#C9A84C]">operational_event_logs</code>{" "}
              filtered by <code className="text-[#C9A84C]">source=&quot;trial&quot;</code>.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="trial-funnel-controls">
            <div className="flex bg-[#181B23] border border-[rgba(255,255,255,0.06)] rounded p-1">
              {DAYS_OPTIONS.map((d) => (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  className={`px-3 py-1 rounded text-[11px] font-medium transition-colors ${
                    days === d
                      ? "bg-[#C9A84C] text-[#0D0F14]"
                      : "text-[#8B95A5] hover:text-white"
                  }`}
                  data-testid={`trial-funnel-window-${d}`}
                >
                  {d}d
                </button>
              ))}
            </div>
            <button
              onClick={fetchAggregates}
              disabled={loading}
              className="px-3 py-1.5 rounded text-[11px] text-[#8B95A5] border border-[rgba(255,255,255,0.06)] hover:text-white flex items-center gap-1.5 disabled:opacity-40"
              data-testid="trial-funnel-refresh"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </header>

        {error && (
          <div className="px-4 py-3 rounded border border-red-500/30 bg-red-500/5 text-red-300 text-[12px] flex items-center gap-2" data-testid="trial-funnel-error">
            <AlertTriangle className="w-3.5 h-3.5" />
            {error}
          </div>
        )}

        {/* PR_RUN_050 — Empty-state CTA. Triggers when the API has
            returned a payload but the window contains zero events —
            common in low-traffic windows or on a fresh tenant.
            Surfaces the marketing acquisition surfaces the admin can
            promote to fill the funnel. */}
        {!loading && data && (data.total_events ?? 0) === 0 && (
          <div
            className="rounded-xl border border-dashed border-[#C9A84C]/30 bg-[#C9A84C]/5 p-6 text-center space-y-3"
            data-testid="trial-funnel-empty-cta"
          >
            <TrendingDown className="w-8 h-8 mx-auto text-[#C9A84C]" />
            <div>
              <h3 className="text-white font-semibold text-sm">
                No trial-funnel events in the last {days} days
              </h3>
              <p className="text-[12px] text-[#8B95A5] mt-1 max-w-md mx-auto">
                Trial signups, checkout intents, and upgrades will appear here
                automatically. To prime the funnel, share a signup surface
                with prospects.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <a
                href="/admin/qr-codes"
                className="px-3 py-1.5 rounded text-[11px] bg-[#C9A84C] text-[#0D0F14] font-medium hover:bg-[#D6B663] flex items-center gap-1.5"
                data-testid="trial-funnel-empty-cta-qr"
              >
                Generate QR signup code
              </a>
              <a
                href="/admin/access-codes"
                className="px-3 py-1.5 rounded text-[11px] text-[#C9A84C] border border-[#C9A84C]/40 hover:bg-[#C9A84C]/10 flex items-center gap-1.5"
                data-testid="trial-funnel-empty-cta-codes"
              >
                Mint enterprise invite
              </a>
              <button
                onClick={() => setDays(365)}
                className="px-3 py-1.5 rounded text-[11px] text-[#8B95A5] border border-[rgba(255,255,255,0.07)] hover:text-white"
                data-testid="trial-funnel-empty-cta-window"
                disabled={days === 365}
              >
                Widen to 365 days
              </button>
            </div>
          </div>
        )}

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="trial-funnel-kpi-strip">
          <KpiCard
            icon={Activity}
            label="Total events"
            value={data?.total_events ?? 0}
            hint={`in last ${days} days`}
          />
          <KpiCard
            icon={Users}
            label="Unique users"
            value={data?.unique_users ?? 0}
            color="#38BDF8"
            hint="touching any funnel stage"
          />
          <KpiCard
            icon={Clock}
            label="Median TTU"
            value={fmtHours(data?.median_time_to_upgrade_hours)}
            color="#10B981"
            hint="signup → upgrade"
          />
          <KpiCard
            icon={TrendingDown}
            label="Upgraded"
            value={stageCounts.upgraded || 0}
            color="#C9A84C"
            hint={`from ${stageCounts.signup || 0} signups`}
          />
        </div>

        {/* Funnel drop-off */}
        <section
          className="rounded-xl border border-[rgba(255,255,255,0.06)] bg-[#181B23] p-5"
          data-testid="trial-funnel-chart"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-sm font-semibold text-white">Conversion drop-off</h2>
              <p className="text-[11px] text-[#8B95A5] mt-0.5">
                Unique users at each stage with step-over-step retention
              </p>
            </div>
          </div>
          {loading && !data ? (
            <div className="text-center text-[#8B95A5] text-[12px] py-10" data-testid="trial-funnel-loading">
              Loading aggregates…
            </div>
          ) : (
            <div className="space-y-3">
              {FUNNEL_ORDER.map((stage, i) => (
                <FunnelBar
                  key={stage}
                  stage={stage}
                  count={stageCounts[stage] || 0}
                  maxCount={funnelMax}
                  prevCount={i === 0 ? 0 : (stageCounts[FUNNEL_ORDER[i - 1]] || 0)}
                  isFirst={i === 0}
                />
              ))}
            </div>
          )}
        </section>

        {/* Source attribution + raw stage counts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Donut */}
          <section
            className="rounded-xl border border-[rgba(255,255,255,0.06)] bg-[#181B23] p-5 lg:col-span-1"
            data-testid="trial-funnel-source-donut"
          >
            <h2 className="text-sm font-semibold text-white mb-1">Source attribution</h2>
            <p className="text-[11px] text-[#8B95A5] mb-3">
              <code>trial_source</code> on signup-stage events
            </p>
            {sourcePieData.length === 0 ? (
              <div className="text-center text-[#8B95A5] text-[12px] py-10">
                No signup events in this window.
              </div>
            ) : (
              <>
                <div style={{ width: "100%", height: 220 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={sourcePieData}
                        innerRadius={50}
                        outerRadius={85}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                        stroke="#0D0F14"
                        strokeWidth={2}
                      >
                        {sourcePieData.map((_, i) => (
                          <Cell key={i} fill={SOURCE_PALETTE[i % SOURCE_PALETTE.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          background: "#0D0F14",
                          border: "1px solid rgba(255,255,255,0.07)",
                          borderRadius: 4,
                          fontSize: 11,
                        }}
                      />
                      <Legend
                        iconSize={8}
                        wrapperStyle={{ fontSize: 11, color: "#8B95A5" }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-center text-[11px] text-[#8B95A5] mt-1">
                  {totalAttributed} attributed signups
                </div>
              </>
            )}
          </section>

          {/* Raw stage counts + sample event ids */}
          <section
            className="rounded-xl border border-[rgba(255,255,255,0.06)] bg-[#181B23] p-5 lg:col-span-2"
            data-testid="trial-funnel-stage-table"
          >
            <h2 className="text-sm font-semibold text-white mb-1">Raw stage counts</h2>
            <p className="text-[11px] text-[#8B95A5] mb-3">
              Click an event ID to jump into the Audit-Trail Explorer with the row pre-filtered.
            </p>
            <div className="space-y-2">
              {Object.entries(stageCounts).map(([stage, count]) => {
                const meta = STAGE_META[stage] || { label: stage, color: "#8B95A5" };
                const samples = (data?.sample_event_ids || {})[stage] || [];
                return (
                  <div
                    key={stage}
                    className="flex items-start justify-between gap-3 py-2 border-b border-[rgba(255,255,255,0.04)] last:border-b-0"
                    data-testid={`trial-funnel-row-${stage}`}
                  >
                    <div className="flex items-center gap-2 min-w-[140px]">
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ background: meta.color }}
                      />
                      {/* PR_RUN_050 — stage label deep-links to the
                          Audit-Trail Explorer pre-filtered by source=trial
                          + action=<stage>. The samples list below still
                          deep-links per event_id via `?q=`. */}
                      <a
                        href={`/audit-explorer?source=trial&action=${stage}`}
                        className="text-[12px] text-white hover:text-[#C9A84C] hover:underline flex items-center gap-1"
                        data-testid={`trial-funnel-row-link-${stage}`}
                        title={`Open Audit-Trail Explorer filtered to source=trial · action=${stage}`}
                      >
                        {meta.label}
                        <ExternalLink className="w-2.5 h-2.5 opacity-50" />
                      </a>
                    </div>
                    <div className="flex-1 flex flex-wrap gap-1">
                      {samples.length === 0 ? (
                        <span className="text-[10px] text-[#555]">no samples</span>
                      ) : (
                        samples.map((id) => (
                          <a
                            key={id}
                            href={`/audit-explorer?q=${id}`}
                            className="font-mono text-[10px] text-[#C9A84C] hover:underline flex items-center gap-1"
                            data-testid={`trial-funnel-sample-${id}`}
                          >
                            {id}
                            <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        ))
                      )}
                    </div>
                    <div className="text-right text-[12px] font-mono text-white min-w-[60px]">
                      {count}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-[#555] mt-4">
              Window since: {data?.since_iso || "—"}
            </p>
          </section>
        </div>
      </div>
    </QMSLayout>
  );
};

export default TrialFunnelPage;
